import React, { useContext, useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { ThemeContext } from "../../../../..";
import { getData } from "../../../../../Services/Services";
import { getBlockInfoFloodURL } from "../../../../../Services/EndPoints";
import BlockPopUp from "../../BlockPopUp";
import DistrictBlockDetails from "../../DistrictBlockDetails";

const FloodDistrictBarChart = ({ title, dataval, startDateStr, endDateStr }) => {
  const { theme } = useContext(ThemeContext);
  const chartRef = useRef(null);
  const [distDetails, setDistDetails] = useState([]);
  const [activeTab, setActiveTab] = useState("Affected");
  const [popUp, setPopUp] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const dataValue = dataval && dataval[0];
  const total = dataValue?.total_dist || 0;
  const affected = dataValue?.affected_dist_count || 0;
  const partiallyAffected = dataValue?.partially_affected || 0;
  const nonAffected = total > (affected + partiallyAffected) ? total - (affected + partiallyAffected) : 0;

  const handleToggle = () => setPopUp(!popUp);

  const columns = React.useMemo(() => [
    { Header: "District Name", accessor: "dist_name", disableSortBy: true, centerAlign: true }
  ], []);

  const fetchData = async (apiKey) => {
    try {
      const response = await getData(
        getBlockInfoFloodURL
          .replace("TODATE", endDateStr)
          .replace("DISTID", 0)
          .replace("APIKEY", apiKey)
          .replace("FROMDATE", startDateStr)
      );
      setDistDetails(response);
    } catch (error) {
      console.error("Error fetching popup data:", error);
    }
  };

  useEffect(() => {
    if (popUp) {
      fetchData(activeTab === "Affected" ? "get_affected_dists" : "get_partially_affected_dists");
    }
  }, [activeTab, popUp]);

  const maxVal = total || 1;
  const items = [
    { label: "Affected", value: affected, pct: total > 0 ? ((affected / total) * 100).toFixed(1) : "0.0", color: "#1C4E72" },
    { label: "Partially Affected", value: partiallyAffected, pct: total > 0 ? ((partiallyAffected / total) * 100).toFixed(1) : "0.0", color: "#51A3EE" },
    { label: "Non Affected", value: nonAffected, pct: total > 0 ? ((nonAffected / total) * 100).toFixed(1) : "0.0", color: "#A0C8E0" }
  ];

  return (
    <div className="card h-100 shadow-sm border-0" style={{ borderRadius: "14px", overflow: "hidden", background: "var(--av-surface)" }}>
      <div className="card-body d-flex flex-column h-100 p-3">
        <div className="card-title fw-bold" style={{ color: "var(--av-primary)", fontSize: "0.80rem", textTransform: "uppercase", letterSpacing: "1px" }}>
          {title}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "14px", padding: "8px 0" }}>
          {items.map((item, idx) => (
            <div key={idx}
              style={{ cursor: "pointer", position: "relative" }}
              onClick={() => { if (item.label !== "Non Affected") { setActiveTab(item.label); setPopUp(true); } }}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {hoveredIdx === idx && (
                <div style={{
                  position: "absolute", top: "-38px", left: "50%", transform: "translateX(-50%)",
                  background: "#1C4E72", color: "#fff", padding: "4px 10px", borderRadius: "8px",
                  fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap", zIndex: 10,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                }}>
                  {item.label}: {item.value} ({item.pct}%)
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "5px" }}>
                <span style={{ fontSize: "11px", fontWeight: "600", color: "#1C4E72" }}>
                  {item.label}
                </span>
                <span style={{ fontSize: "13px", fontWeight: "800", color: "#0284C7" }}>{item.value}
                </span>
              </div>
              <div style={{
                width: "100%", height: "18px", backgroundColor: "#EBF2F8",
                borderRadius: "9px", overflow: "hidden", position: "relative"
              }}>
                <div style={{
                  width: `${Math.max((item.value / maxVal) * 100, 2)}%`,
                  height: "100%",
                  background: `linear-gradient(90deg, ${item.color}, ${item.color}cc)`,
                  borderRadius: "9px",
                  transition: "width 0.8s ease-in-out",
                  display: "flex", alignItems: "center", paddingLeft: "8px"
                }}>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "16px", justifyContent: "center", paddingTop: "6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: "#1C4E72" }} />
            <span style={{ fontSize: "10px", fontWeight: "600", color: "#1C4E72" }}>Affected</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: "#51A3EE" }} />
            <span style={{ fontSize: "10px", fontWeight: "600", color: "#1C4E72" }}>Partially Affected</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: "#A0C8E0" }} />
            <span style={{ fontSize: "10px", fontWeight: "600", color: "#1C4E72" }}>Non Affected</span>
          </div>
        </div>
      </div>
      {popUp && (
        <BlockPopUp title={`${title} Details`} show={popUp} handleToggle={handleToggle}
          component={<DistrictBlockDetails data={distDetails} column={columns} activeTab={activeTab} setActiveTab={setActiveTab} />}
        />
      )}
    </div>
  );
};

export default FloodDistrictBarChart;
